import { useState, useRef } from 'react';
import {
  X,
  AlertCircle,
  Copy,
  Trash2,
  UploadCloud,
  FileText,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import { useChangeStatusMutation } from '@/features/orders/orders.queries';
import type { Order, ChangeStatusResponse } from '@/features/orders/orders.types';

interface ChangeStatusModalProps {
  open: boolean;
  onClose: () => void;
  orders: Order[];
}

export function ChangeStatusModal(props: ChangeStatusModalProps) {
  const { open, onClose, orders } = props;
  const [motivo, setMotivo] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [result, setResult] = useState<ChangeStatusResponse | null>(null);
  const file_input_ref = useRef<HTMLInputElement>(null);

  const mutation = useChangeStatusMutation();

  if (!open) return null;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setEvidence(file);
    } else if (file) {
      toast.error('Solo se permiten archivos de imagen.');
    }
  };

  const onSubmit = () => {
    mutation.mutate(
      {
        order_refs: orders.map((o) => o.external_reference),
        external_reference: orders[0]?.external_reference,
        action: 'delete',
        motivo,
        evidence_file: evidence || undefined,
      },
      {
        onSuccess: (data) => {
          if (data.blocked.length === 0 && data.not_found.length === 0) {
            onClose();
          } else {
            setResult(data);
          }
        },
      },
    );
  };

  const onCopyText = () => {
    if (!result) return;
    const text = [
      'Órdenes que no se pudieron eliminar:',
      ...result.blocked.map(
        (b) => `- ${b.ref} — ${b.status_name || b.reason} (Status: ${b.current_status})`,
      ),
      ...result.not_found.map((ref) => `- ${ref} — No encontrada`),
      '\nSi necesitas ayuda, manda un ticket a ti@99minutos.com con esta información.',
    ].join('\n');

    navigator.clipboard.writeText(text);
    toast.success('Texto copiado al portapapeles');
  };

  const is_loading = mutation.isPending;
  const is_submit_disabled = is_loading || motivo.trim().length < 5;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-lg">
        <div className="flex items-center justify-between border-b border-base-300 pb-4 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-error" />
            Eliminar {orders.length} {orders.length === 1 ? 'orden' : 'órdenes'}
          </h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={is_loading}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {!result ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-bold text-base-content/50 uppercase">
                Órdenes seleccionadas
              </span>
              <div className="max-h-32 overflow-y-auto border border-base-300 rounded-lg p-2 bg-base-200/50">
                <ul className="flex flex-wrap gap-2">
                  {orders.map((o) => (
                    <li key={o.id} className="badge badge-neutral gap-1 font-mono text-xs">
                      {o.tracking_number || o.external_reference}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Motivo de eliminación *</label>
              <textarea
                className="textarea textarea-bordered h-24 w-full"
                placeholder="Explica por qué se elimina la orden..."
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                disabled={is_loading}
              />
              <p className="text-[10px] text-base-content/40">Mínimo 5 caracteres.</p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Evidencia (opcional)</label>
              <div
                className="border-2 border-dashed border-base-300 rounded-lg p-4 flex flex-col items-center justify-center gap-2 hover:bg-base-200 transition-colors cursor-pointer"
                onClick={() => !is_loading && file_input_ref.current?.click()}
              >
                {evidence ? (
                  <div className="flex items-center gap-2 text-primary font-medium text-sm">
                    <FileText className="h-5 w-5" />
                    {evidence.name}
                  </div>
                ) : (
                  <>
                    <UploadCloud className="h-8 w-8 text-base-content/20" />
                    <span className="text-xs text-base-content/60">Haz click para subir imagen</span>
                  </>
                )}
                <input
                  ref={file_input_ref}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={onFileChange}
                  disabled={is_loading}
                />
              </div>
            </div>

            <div className="modal-action">
              <button className="btn btn-ghost" onClick={onClose} disabled={is_loading}>
                Cancelar
              </button>
              <button
                className="btn btn-error text-white"
                disabled={is_submit_disabled}
                onClick={onSubmit}
              >
                {is_loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Confirmar eliminación
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="alert alert-warning flex flex-col items-start gap-2 text-left py-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span className="font-bold">Acción completada con observaciones</span>
              </div>
              <p className="text-xs">Algunas órdenes no pudieron procesarse.</p>
            </div>

            <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
              {result.blocked.map((b) => (
                <div key={b.ref} className="text-xs border-l-4 border-error pl-3 py-1 bg-error/5">
                  <span className="font-bold font-mono">{b.ref}</span>
                  <p className="text-base-content/70">
                    {b.reason === 'not_cancellable'
                      ? 'Su estado actual no permite cancelarla'
                      : b.reason === 'sot_unavailable'
                        ? 'No se pudo validar su estado en 99minutos, intenta de nuevo'
                        : b.reason}
                    {b.status_name ? ` (${b.status_name})` : ''}
                  </p>
                </div>
              ))}
              {result.not_found.map((ref) => (
                <div key={ref} className="text-xs border-l-4 border-neutral pl-3 py-1 bg-neutral/5">
                  <span className="font-bold font-mono">{ref}</span>
                  <p className="text-base-content/70">No encontrada en el sistema.</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2 pt-2 border-t border-base-300">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-base-content/50 uppercase">
                  Resumen para soporte
                </span>
                <button className="btn btn-ghost btn-xs gap-1" onClick={onCopyText}>
                  <Copy className="h-3 w-3" />
                  Copiar
                </button>
              </div>
              <textarea
                readOnly
                className="textarea textarea-bordered text-[10px] h-20 bg-base-200"
                value={[
                  ...result.blocked.map(
                    (b) => `- ${b.ref} — ${b.status_name || b.reason} (Status: ${b.current_status})`,
                  ),
                  ...result.not_found.map((ref) => `- ${ref} — No encontrada`),
                ].join('\n')}
              />
              <p className="text-[10px] text-base-content/60 italic">
                Manda un ticket a <span className="font-bold">ti@99minutos.com</span> con esta
                información.
              </p>
            </div>

            <div className="modal-action">
              <button className="btn btn-primary" onClick={onClose}>
                Entendido
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="modal-backdrop bg-black/50 backdrop-blur-sm" onClick={is_loading ? undefined : onClose} />
    </dialog>
  );
}
